<!--
  SelectValueField — value `select` renderer; the chrome-free inner GridCell
  hosts. Inline shows the selected option's label + chevron; active/editing is a
  keyboard-navigable option list (arrows, Enter/Space/click), live only when
  `mode === "editing"`. `modelValue` is the raw stored value; options from
  `field.values`. Picking emits the value and commits. (Reference analogue:
  SelectReferenceField, same UX but store-sourced options.)
-->
<template>
  <div v-if="mode === 'inline'" class="select-value-field" :class="sizeClass">
    <span class="select-value">{{ displayLabel }}</span>
    <span class="select-chevron icon is-small">
      <font-awesome-icon icon="chevron-down" size="xs" />
    </span>
  </div>
  <div v-else class="select-value-field" :class="sizeClass">
    <template v-if="mode !== 'editing'">
      <span class="select-value">{{ displayLabel }}</span>
      <span class="select-chevron icon is-small">
        <font-awesome-icon icon="chevron-down" size="xs" />
      </span>
    </template>
    <div
      v-else
      ref="dropdownRef"
      class="select-dropdown"
      tabindex="-1"
      @keydown="onDropdownKeydown"
      @mousedown.prevent
      @click.stop
    >
      <div
        v-for="(opt, index) in options"
        :key="opt.value"
        class="select-option"
        :class="[
          sizeClass,
          {
            'is-selected': opt.value === modelValue,
            'is-highlighted': index === highlightedIndex,
          },
        ]"
        @click.stop="selectValue(opt.value)"
        @mouseenter="highlightedIndex = index"
      >
        {{ opt.label }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { type Field, selectOptions } from "@auger/core/fields"
import { type FieldMode, type FieldSize, textSizeClass } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const options = computed(() => selectOptions(props.field))
const sizeClass = computed(() => textSizeClass(props.size))

const displayLabel = computed(() => {
  const v = props.modelValue
  if (v == null || v === "") return ""
  const opt = options.value.find((o) => o.value === v)
  return opt ? opt.label : String(v)
})

// On edit-open, highlight the current value and focus the list for arrow keys.
const dropdownRef = ref<HTMLElement | null>(null)
const highlightedIndex = ref(0)
watch(
  () => props.mode === "editing",
  (on) => {
    if (!on) return
    const idx = options.value.findIndex((o) => o.value === props.modelValue)
    highlightedIndex.value = idx >= 0 ? idx : 0
    nextTick(() => dropdownRef.value?.focus())
  }
)

// Picking is atomic: emit the chosen value if it's a real option, then close.
function selectValue(value: string | number) {
  if (!options.value.some((o) => o.value === value)) {
    emit("cancel")
    return
  }
  emit("update:modelValue", value)
  emit("commit")
}

function onDropdownKeydown(event: KeyboardEvent) {
  const opts = options.value
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    event.stopPropagation()
    const opt = opts[highlightedIndex.value]
    if (opt) selectValue(opt.value)
    return
  }
  if (event.key === "ArrowDown") {
    event.preventDefault()
    event.stopPropagation()
    highlightedIndex.value = Math.min(highlightedIndex.value + 1, opts.length - 1)
    scrollHighlightedIntoView()
    return
  }
  if (event.key === "ArrowUp") {
    event.preventDefault()
    event.stopPropagation()
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
    scrollHighlightedIntoView()
  }
}

function scrollHighlightedIntoView() {
  nextTick(() => {
    dropdownRef.value?.querySelector(".is-highlighted")?.scrollIntoView({ block: "nearest" })
  })
}
</script>

<style>
.select-value-field {
  display: flex;
  width: 100%;
  align-items: center;
  cursor: default;

  .select-value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .select-chevron {
    opacity: 0;
    margin-left: auto;
    flex-shrink: 0;
    cursor: pointer;
  }

  &:hover .select-chevron {
    opacity: 1;
  }
}

.grid-editor .select-value-field {
  margin-top: -0.5px;
  margin-left: 1px;
}
.grid-editor .select-value-field .select-chevron {
  opacity: 1;
}

/* The dropdown lives inside the floating `.grid-editor` box, which provides the
   frame (border / padding / shadow / bg). The dropdown owns only the vertical
   option list: stack the options and scroll. Scoped under the field root so it
   stays independent of SelectReferenceCell's parallel dropdown. */
.select-value-field .select-dropdown {
  display: block;
  max-height: 240px;
  overflow-y: auto;
  outline: none;
  width: 100%;

  .select-option {
    padding: 4px 6px;
    cursor: pointer;

    &.is-highlighted {
      background: var(--auger-link, #485fc7);
      color: var(--auger-text-inverted);
      border-radius: var(--auger-radius);
    }

    &.is-selected {
      font-weight: 600;
    }
  }
}
</style>
